import { Fragment, useCallback, useEffect } from 'react';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import ContentCardSkeleton from '../../../components/TrackCard/ContentCardSkeleton';
import UsageTracker from '../../../utils/UsageTracker';
import { SCREENS, SUB_SCREENS } from '../../../constants/Tracking';
import ApiError from '../../../components/Errors/ApiError';
import { RecommendationsGrid } from '../components/RecommendationsGrid';
import { useRecommendationsForTrack } from '../recommendationsQueries';
import SectionHeading from '../../../components/SectionHeading';
import { Section } from 'foundations-components/transitional/components/Section';
import { View } from 'foundations-components/components/View';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import LearningContentCard from '../../cards/components/LearningContentCard';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import getLang from 'academy-common-lib/utils/getLang';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useRecommendedContentModels = (trackId, language) => {
  const {
    recommendationsForTrack,
    recommendationsForTrackLoading,
    recommendationsForTrackError
  } = useRecommendationsForTrack(trackId);
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items: (recommendationsForTrack === null || recommendationsForTrack === void 0 ? void 0 : recommendationsForTrack.recommendations.map(({
      recommendationId,
      recommendationType,
      recommendationStrategy,
      recommendationMetadata: {
        preferredTopic,
        entityType,
        entityId
      }
    }) => {
      return {
        id: recommendationId,
        type: recommendationType,
        language: language || getLang(),
        recommendationStrategy,
        preferredTopic: preferredTopic,
        recommendedBecause: {
          id: entityId,
          type: entityType
        }
      };
    })) || [],
    skip: !(recommendationsForTrack !== null && recommendationsForTrack !== void 0 && recommendationsForTrack.recommendations.length)
  });
  return {
    recommendations: cardModels,
    isRequesting: cardModelsLoading || recommendationsForTrackLoading,
    error: cardModelsError || recommendationsForTrackError
  };
};
export const TrackRecommendations = ({
  trackId,
  language,
  name
}) => {
  useEffect(() => {
    UsageTracker.track('subpageView', {
      screen: SCREENS.TRACK,
      subscreen: SUB_SCREENS.RECOMMENDATIONS,
      trackId
    });
  }, [trackId]);
  const {
    recommendations,
    isRequesting,
    error
  } = useRecommendedContentModels(trackId, language);
  const trackRecommendedCardClick = useCallback(({
    id,
    type,
    recommendationStrategy,
    preferredTopic,
    recommendedBecause
  }, index) => () => {
    UsageTracker.track('interaction', {
      action: 'track-end-recommendation-card-click',
      cardNumber: index + 1,
      lessonId: type === 'LESSON' && id,
      trackId: type === 'TRACK' && id,
      recommendationStrategy,
      preferredTopic,
      recommendedBecauseTrackId: recommendedBecause.type === 'TRACK' && recommendedBecause.id,
      recommendedBecauseLessonId: recommendedBecause.type === 'LESSON' && recommendedBecause.id
    });
  }, []);
  const {
    isTablet
  } = useMediaQuery();
  if (error) {
    return /*#__PURE__*/_jsx(ApiError, {
      error: error
    });
  }
  if (!isRequesting && !recommendations) {
    return null;
  }
  return /*#__PURE__*/_jsx(View, {
    "data-test-id": "track-recommendations-page",
    marginBlockStart: "600",
    paddingInline: isTablet ? '300' : '700',
    children: /*#__PURE__*/_jsxs(Section, {
      children: [/*#__PURE__*/_jsx(View, {
        as: "div",
        marginBlockEnd: "600",
        children: /*#__PURE__*/_jsx(SectionHeading, {
          subText: name && /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "learningCenter.tracks.recommendations.courseRecommendations.body",
            options: {
              trackName: name
            }
          }),
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "learningCenter.tracks.recommendations.courseRecommendations.heading"
          })
        })
      }), /*#__PURE__*/_jsx(RecommendationsGrid, {
        children: isRequesting ? /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(ContentCardSkeleton, {}), /*#__PURE__*/_jsx(ContentCardSkeleton, {}), /*#__PURE__*/_jsx(ContentCardSkeleton, {})]
        }) : recommendations === null || recommendations === void 0 ? void 0 : recommendations.slice(0, 3).map((recommendation, index) => /*#__PURE__*/_jsx(LearningContentCard, {
          item: recommendation,
          onItemClick: trackRecommendedCardClick(recommendation, index),
          overviewType: "recommendations",
          position: index + 1
        }, recommendation.id))
      })]
    })
  });
};